/**
 * Login page component.
 *
 * Flow: Email → method selection (passkey or password) → MFA if needed.
 *
 * Supports mobile login flow via ?mobile=true&callback=<url> parameters.
 * When mobile=true, after successful login a mobile token is fetched
 * and the user is redirected to the callback URL with the token.
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import type { LoginFlow, UiNodeInputAttributes } from '@ory/client';
import { kratos, getFlowId, getReturnTo, redirectTo, handleKratosError } from '../lib/kratos';
import { useAuth } from '../context/AuthContext';
import FormField from '../components/FormField';
import FlowMessages from '../components/FlowMessages';
import LoadingSpinner from '../components/LoadingSpinner';
import QRLogin from '../components/QRLogin';

type LoginPhase = 'identifier' | 'password' | 'webauthn' | 'totp' | 'lookup_secret' | 'code' | 'approve';
type LoginTab = 'credentials' | 'qr';

import { config } from '../config';
import {
  steerAssertion, canUsePhonePasskey, PENDING_TOTP_KEY, PENDING_TOTP_MAX_AGE_MS, PHONE_PASSKEY_UNAVAILABLE,
  type AuthenticatorMode,
} from '../lib/webauthn';
import { LOGO_MARK_LIGHT, LOGO_MARK_DARK, LOGO_ALT } from '../brand';
// Gateway URL for mobile token exchange (must match config.gatewayUrl)
const GATEWAY_URL = config.gatewayUrl;

export default function Login() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading, needsAal2, logout } = useAuth();
  const [flow, setFlow] = useState<LoginFlow | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loginPhase, setLoginPhase] = useState<LoginPhase>('identifier');
  const [totpCode, setTotpCode] = useState('');
  const [lookupCode, setLookupCode] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [hasPasskey, setHasPasskey] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [loginTab, setLoginTab] = useState<LoginTab>('credentials');
  const [phoneApproval, setPhoneApproval] = useState<{ id: string; matchNumber: string; secret: string } | null>(null);
  const [phonePasskey, setPhonePasskey] = useState<boolean | null>(null);

  useEffect(() => { void canUsePhonePasskey().then(setPhonePasskey); }, []);

  // Check if this is a mobile login request
  const params = new URLSearchParams(location.search);
  const isMobileLogin = params.get('mobile') === 'true';
  const mobileCallback = params.get('callback');
  const forceRefresh = params.get('refresh') === 'true';
  // A half-finished login (AAL1 session, second factor pending) must continue at
  // AAL2 — a plain login flow is rejected by Kratos with "already logged in".
  const requestedAal = params.get('aal') || (needsAal2 ? 'aal2' : null);

  // Capture returnTo once from the initial URL — do NOT recompute on every
  // location.search change, or the useCallback/useEffect chain will loop.
  const returnToRef = useRef<string | null>(null);
  if (returnToRef.current === null) {
    returnToRef.current = isMobileLogin && mobileCallback
      ? `${window.location.origin}/mobile-token-exchange?callback=${encodeURIComponent(mobileCallback)}`
      : getReturnTo(location.search);
  }
  const returnTo = returnToRef.current;

  const detectLoginPhase = (flowData: LoginFlow): LoginPhase => {
    const totpNodes = flowData.ui.nodes.filter((n) => n.group === 'totp');
    if (totpNodes.some((n) => (n.attributes as UiNodeInputAttributes).name === 'totp_code')) {
      return 'totp';
    }

    const webauthnNodes = flowData.ui.nodes.filter((n) => n.group === 'webauthn');
    const hasWebauthnTrigger = webauthnNodes.some((n) => {
      const a = n.attributes as UiNodeInputAttributes;
      return a.name === 'webauthn_login_trigger';
    });

    if (hasWebauthnTrigger) {
      setHasPasskey(true);
      return 'webauthn';
    }

    // Passwordless email OTP — code group with code input (after OTP was sent)
    const codeNodes = flowData.ui.nodes.filter((n) => n.group === 'code');
    if (codeNodes.some((n) => (n.attributes as UiNodeInputAttributes).name === 'code')) {
      return 'code';
    }

    const lookupNodes = flowData.ui.nodes.filter((n) => n.group === 'lookup_secret');
    if (lookupNodes.some((n) => (n.attributes as UiNodeInputAttributes).name === 'lookup_secret')) {
      return 'lookup_secret';
    }

    // If code method is available (submit button present), show identifier phase
    // so the user can choose between password and email code
    const hasCodeMethod = codeNodes.some((n) => (n.attributes as UiNodeInputAttributes).name === 'method');
    if (hasCodeMethod) {
      return 'identifier';
    }

    // If webauthn method node exists (but no trigger yet), start with identifier
    const hasWebauthnMethod = webauthnNodes.length > 0;
    if (hasWebauthnMethod && !identifier) {
      return 'identifier';
    }

    return 'password';
  };

  const initializeFlow = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const flowId = getFlowId(location.search);

      if (flowId) {
        try {
          const { data } = await kratos.getLoginFlow({ id: flowId });
          setFlow(data);
          setLoginPhase(detectLoginPhase(data));
          return;
        } catch (flowErr) {
          // 403 = CSRF mismatch (stale cookies after Kratos restart).
          // Clear Kratos cookies and reload once to break the loop.
          const resp = (flowErr as { response?: { status?: number } })?.response;
          if (resp?.status === 403) {
            document.cookie.split(';').forEach((c) => {
              const name = c.split('=')[0].trim();
              if (name.startsWith('csrf_token') || name === 'ory_kratos_session' || name === 'passport_session') {
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname.split('.').slice(-2).join('.')}`;
                document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
              }
            });
            // Reload once — clean cookies will let the new flow work
            window.location.replace(window.location.href);
            return;
          }
          // Other errors (expired flow, etc.) — create fresh
        }
      }

      const { data } = await kratos.createBrowserLoginFlow({
        returnTo,
        refresh: forceRefresh,
        aal: requestedAal || undefined,
      });
      const newParams = new URLSearchParams();
      newParams.set('flow', data.id);
      if (returnTo && returnTo !== '/organizations') {
        newParams.set('return_to', returnTo);
      }
      if (isMobileLogin) {
        newParams.set('mobile', 'true');
        if (mobileCallback) newParams.set('callback', mobileCallback);
      }
      navigate(`/login?${newParams.toString()}`, { replace: true });
      setFlow(data);
      setLoginPhase(detectLoginPhase(data));
    } catch (err) {
      setError(handleKratosError(err));
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, navigate, isMobileLogin, mobileCallback, forceRefresh, requestedAal]);

  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated && !forceRefresh && !requestedAal) {
      if (redirecting) return;
      setRedirecting(true);
      if (isMobileLogin && mobileCallback) {
        fetchMobileToken(mobileCallback);
        return;
      }
      redirectTo(returnTo, navigate);
      return;
    }
    initializeFlow();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initializeFlow, isAuthenticated, authLoading, navigate, isMobileLogin, mobileCallback, forceRefresh, requestedAal]);

  // ============================================================
  // Helpers
  // ============================================================

  const getCsrfToken = (): string | undefined => {
    const node = flow?.ui.nodes.find((n) => {
      const a = n.attributes as UiNodeInputAttributes;
      return a.name === 'csrf_token';
    });
    return node ? (node.attributes as UiNodeInputAttributes).value as string : undefined;
  };

  const handleLoginSuccess = async (data: { session?: unknown }) => {
    if (data.session) {
      if (isMobileLogin && mobileCallback) {
        await fetchMobileToken(mobileCallback);
        return;
      }
      window.location.href = returnTo;
    }
  };

  const handle422Redirect = (err: unknown): boolean => {
    if (err && typeof err === 'object' && 'response' in err) {
      const response = (err as { response?: { status?: number; data?: { redirect_browser_to?: string } } }).response;
      if (response?.status === 422 && response?.data?.redirect_browser_to) {
        const redirectUrl = response.data.redirect_browser_to;
        const url = new URL(redirectUrl);

        // Extract the flow ID from the redirect URL — Kratos already created the flow
        const redirectFlowId = url.searchParams.get('flow');
        const aal = url.searchParams.get('aal');

        (async () => {
          try {
            if (redirectFlowId) {
              // Fetch the existing flow — it should have the webauthn challenge
              const { data } = await kratos.getLoginFlow({ id: redirectFlowId });

              const newParams = new URLSearchParams();
              newParams.set('flow', redirectFlowId);
              if (isMobileLogin) {
                newParams.set('mobile', 'true');
                if (mobileCallback) newParams.set('callback', mobileCallback);
              }
              navigate(`/login?${newParams.toString()}`, { replace: true });

              setFlow(data);
              const phase = detectLoginPhase(data);
              setLoginPhase(phase);
            } else if (aal) {
              // AAL upgrade — create new flow
              const newParams = new URLSearchParams();
              newParams.set('aal', aal);
              navigate(`/login?${newParams.toString()}`, { replace: true });
            }
          } catch {
            // Fallback — navigate to the redirect URL directly
            window.location.href = redirectUrl;
          } finally {
            setIsSubmitting(false);
          }
        })();
        return true;
      }
    }
    return false;
  };

  const fetchMobileToken = async (callback: string): Promise<void> => {
    try {
      const response = await fetch(`${GATEWAY_URL}/v1/auth/mobile-token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          device_name: navigator.userAgent.includes('Mobile') ? 'Mobile Device' : 'Unknown Device',
        }),
      });
      if (!response.ok) throw new Error('Failed to get mobile token');
      const data = await response.json();
      const params = new URLSearchParams();
      params.set('token', data.token);
      if (data.user) {
        try { params.set('user', btoa(JSON.stringify(data.user))); } catch { /* skip */ }
      }
      const isCustomScheme = callback.includes('://') && !callback.startsWith('http');
      if (isCustomScheme) {
        window.location.href = `${callback}?${params.toString()}`;
        return;
      }
      let path = '/mobile-callback';
      try { path = new URL(callback).pathname; } catch { /* default */ }
      navigate(`${path}?${params.toString()}`, { replace: true });
    } catch (err) {
      console.error('fetchMobileToken error:', err);
      setError('Failed to generate mobile token. Please try again.');
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // Phase: Identifier — submit email to Kratos with method=webauthn
  // If user has passkey, Kratos returns 422 → new flow with challenge
  // If not, we fall through to password
  // ============================================================

  const handleIdentifierSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const email = (event.currentTarget.querySelector('input[name="identifier"]') as HTMLInputElement)?.value
      || (event.currentTarget.querySelector('input[type="email"]') as HTMLInputElement)?.value
      || identifier;

    if (!email) {
      setError('Please enter your email address');
      setIsSubmitting(false);
      return;
    }

    setIdentifier(email);

    try {
      // Try webauthn method first — if user has a passkey, Kratos will return 422 redirect
      const { data } = await kratos.updateLoginFlow({
        flow: flow!.id,
        updateLoginFlowBody: {
          method: 'webauthn',
          identifier: email,
          csrf_token: getCsrfToken(),
        } as unknown as Parameters<typeof kratos.updateLoginFlow>[0]['updateLoginFlowBody'],
      });

      // If we get here without error, login succeeded (unlikely for identifier-only)
      await handleLoginSuccess(data);
    } catch (err) {
      // 422 = Kratos wants browser redirect → new flow with webauthn challenge
      if (handle422Redirect(err)) return;

      // 400 = no webauthn credential for this user → fall to password
      setLoginPhase('password');
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // Phase: Password submit
  // ============================================================

  const handlePasswordSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const formData = new FormData(event.currentTarget);
    const values: Record<string, string> = {};
    formData.forEach((value, key) => { values[key] = value.toString(); });

    // Ensure identifier is set
    if (!values.identifier && identifier) {
      values.identifier = identifier;
    }

    try {
      const { data } = await kratos.updateLoginFlow({
        flow: flow!.id,
        updateLoginFlowBody: values as unknown as Parameters<typeof kratos.updateLoginFlow>[0]['updateLoginFlowBody'],
      });

      if ('session' in data && data.session) {
        await handleLoginSuccess(data);
        return;
      }

      // MFA required
      const flowData = data as unknown as LoginFlow;
      if ('ui' in data) {
        const newPhase = detectLoginPhase(flowData);
        if (newPhase !== 'password' && newPhase !== 'identifier') {
          setFlow(flowData);
          setLoginPhase(newPhase);
          setIsSubmitting(false);
          return;
        }
        setFlow(flowData);
      }
    } catch (err) {
      if (handle422Redirect(err)) return;

      // On 400, Kratos returns the updated flow with error messages in ui.messages/nodes
      if (err && typeof err === 'object' && 'response' in err) {
        const resp = (err as { response?: { status?: number; data?: LoginFlow & { error?: { message?: string } } } }).response;
        if (resp?.status === 400 && resp.data?.ui) {
          // Update flow to show Kratos error messages via FlowMessages
          setFlow(resp.data as LoginFlow);
          setError(null); // Let FlowMessages handle it
          setIsSubmitting(false);
          return;
        }
      }

      const errorMessage = handleKratosError(err);
      if (errorMessage.includes('expired')) {
        initializeFlow();
      }
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // Phase: WebAuthn — passkey ceremony
  // ============================================================

  const handlePasskeyLogin = async (mode: AuthenticatorMode = 'any') => {
    if (!flow) return;
    setIsSubmitting(true);
    setError(null);

    const webauthnNodes = flow.ui.nodes.filter((n) => n.group === 'webauthn');
    const loginTrigger = webauthnNodes.find((n) => {
      const a = n.attributes as UiNodeInputAttributes;
      return a.name === 'webauthn_login_trigger';
    });
    const triggerAttrs = loginTrigger?.attributes as unknown as { onclick?: string } | undefined;
    const onclickCode = triggerAttrs?.onclick || '';
    const optMatch = onclickCode.match(/window\.__oryWebAuthnLogin\((.+)\)/s);

    if (!optMatch) {
      setError('WebAuthn not available');
      setIsSubmitting(false);
      return;
    }

    try {
      const opt = JSON.parse(optMatch[1]);
      const b64Decode = (v: string) =>
        Uint8Array.from(atob(v.replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));
      const b64Encode = (v: ArrayBuffer) =>
        btoa(String.fromCharCode(...new Uint8Array(v)))
          .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');

      opt.publicKey.challenge = b64Decode(opt.publicKey.challenge);
      if (opt.publicKey.allowCredentials) {
        opt.publicKey.allowCredentials = opt.publicKey.allowCredentials.map(
          (c: { id: string }) => ({ ...c, id: b64Decode(c.id) })
        );
      }
      steerAssertion(opt.publicKey, mode);

      const credential = await navigator.credentials.get(opt) as PublicKeyCredential;
      const response = credential.response as AuthenticatorAssertionResponse;

      const webauthnLogin = JSON.stringify({
        id: credential.id,
        rawId: b64Encode(credential.rawId),
        type: credential.type,
        response: {
          authenticatorData: b64Encode(response.authenticatorData),
          clientDataJSON: b64Encode(response.clientDataJSON),
          signature: b64Encode(response.signature),
          userHandle: b64Encode(response.userHandle || new ArrayBuffer(0)),
        },
      });

      // Build a native form and submit (browser flow)
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = flow.ui.action;
      form.style.display = 'none';

      // Add hidden fields
      const addField = (name: string, value: string) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
      };

      // CSRF + default hidden fields
      for (const node of flow.ui.nodes) {
        if (node.group === 'default' && (node.attributes as { type?: string }).type === 'hidden') {
          const a = node.attributes as UiNodeInputAttributes;
          addField(a.name, a.value as string);
        }
      }

      addField('webauthn_login', webauthnLogin);

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      if (err instanceof DOMException && err.name === 'NotAllowedError') {
        setError('Passkey authentication was cancelled.');
      } else {
        setError(handleKratosError(err));
      }
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // Phase: TOTP
  // ============================================================

  const handleTOTPSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await submitTotpCode(totpCode);
  };

  // Shared by typed codes, phone approval and QR login handoff (OPP-144)
  const submitTotpCode = async (code: string) => {
    setIsSubmitting(true);
    setError(null);

    try {
      const { data } = await kratos.updateLoginFlow({
        flow: flow!.id,
        updateLoginFlowBody: {
          method: 'totp',
          totp_code: code,
          csrf_token: getCsrfToken(),
        } as Parameters<typeof kratos.updateLoginFlow>[0]['updateLoginFlowBody'],
      });
      if (data.session) {
        await handleLoginSuccess(data);
      }
    } catch (err) {
      setError(handleKratosError(err));
      if (flow) {
        try {
          const { data } = await kratos.getLoginFlow({ id: flow.id });
          setFlow(data);
        } catch { /* ignore */ }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // Second step (AAL2): every method the account has is selectable
  // ============================================================

  const isAal2 = flow?.requested_aal === 'aal2';
  const flowHas = (group: string, name: string) =>
    !!flow?.ui.nodes.some((n) => n.group === group && (n.attributes as UiNodeInputAttributes).name === name);
  const secondStep = {
    totp: flowHas('totp', 'totp_code'),
    passkey: flowHas('webauthn', 'webauthn_login_trigger'),
    backup: flowHas('lookup_secret', 'lookup_secret'),
  };
  const flowIdentifier = (flow?.ui.nodes.find(
    (n) => n.group === 'default' && (n.attributes as UiNodeInputAttributes).name === 'identifier',
  )?.attributes as UiNodeInputAttributes | undefined)?.value as string | undefined;

  const startPhoneApproval = async () => {
    if (!flow) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const resp = await fetch(`${GATEWAY_URL}/v1/auth/login-approvals`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ flow_id: flow.id }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(typeof body.detail === 'string' ? body.detail : 'Could not reach your phone');
      setPhoneApproval({ id: body.approval_id, matchNumber: body.match_number, secret: body.browser_secret });
      setLoginPhase('approve');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not reach your phone');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Poll the phone approval; the app's code arrives once and goes to Kratos
  useEffect(() => {
    if (loginPhase !== 'approve' || !phoneApproval) return;
    const timer = window.setInterval(async () => {
      try {
        const resp = await fetch(`${GATEWAY_URL}/v1/auth/login-approvals/${phoneApproval.id}`, {
          headers: { 'X-Approval-Secret': phoneApproval.secret },
        });
        if (!resp.ok) return;
        const body = await resp.json();
        if (body.status === 'approved' && body.totp_code) {
          window.clearInterval(timer);
          await submitTotpCode(body.totp_code);
        } else if (body.status === 'denied' || body.status === 'expired') {
          window.clearInterval(timer);
          setPhoneApproval(null);
          setError(body.status === 'denied' ? 'Sign-in was denied on your phone.' : 'The approval request expired. Try again.');
        }
      } catch {
        // transient — keep polling
      }
    }, 2000);
    return () => window.clearInterval(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginPhase, phoneApproval]);

  // QR login handoff: the phone approved and sent its code with the approval
  useEffect(() => {
    if (!flow || !isAal2 || !secondStep.totp) return;
    let pending: { code?: string; at?: number } | null = null;
    try {
      pending = JSON.parse(sessionStorage.getItem(PENDING_TOTP_KEY) || 'null');
      sessionStorage.removeItem(PENDING_TOTP_KEY);
    } catch { /* storage unavailable */ }
    if (pending?.code && pending.at && Date.now() - pending.at < PENDING_TOTP_MAX_AGE_MS) {
      void submitTotpCode(pending.code);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow?.id]);

  const SecondStepOptions = ({ current }: { current: LoginPhase }) => isAal2 ? (
    <div className="mt-6 pt-4 border-t border-border space-y-2 text-center">
      <p className="text-xs text-content-tertiary">Other ways to confirm it's you</p>
      {secondStep.totp && current !== 'approve' && (
        <button type="button" onClick={startPhoneApproval} disabled={isSubmitting}
          className="block w-full text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300">
          Approve on my phone
        </button>
      )}
      {secondStep.totp && current !== 'totp' && (
        <button type="button" onClick={() => { setError(null); setLoginPhase('totp'); }}
          className="block w-full text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300">
          Enter a code from my authenticator app
        </button>
      )}
      {secondStep.passkey && current !== 'webauthn' && (
        <button type="button" onClick={() => { setError(null); setLoginPhase('webauthn'); }}
          className="block w-full text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300">
          Use a passkey
        </button>
      )}
      {secondStep.backup && current !== 'lookup_secret' && (
        <button type="button" onClick={() => { setError(null); setLoginPhase('lookup_secret'); }}
          className="block w-full text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300">
          Use a backup code
        </button>
      )}
      <button type="button" onClick={() => { void logout(); }}
        className="block w-full text-sm text-content-tertiary hover:text-content-secondary">
        Not you? Sign out and start over
      </button>
    </div>
  ) : null;

  // ============================================================
  // Phase: Backup codes
  // ============================================================

  const handleLookupSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const { data } = await kratos.updateLoginFlow({
        flow: flow!.id,
        updateLoginFlowBody: {
          method: 'lookup_secret',
          lookup_secret: lookupCode,
          csrf_token: getCsrfToken(),
        } as Parameters<typeof kratos.updateLoginFlow>[0]['updateLoginFlowBody'],
      });
      if (data.session) {
        await handleLoginSuccess(data);
      }
    } catch (err) {
      setError(handleKratosError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // Render
  // ============================================================

  if (isLoading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner text="Loading..." />
      </div>
    );
  }

  if (error && !flow) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <h1 className="text-xl font-bold text-red-600 mb-2">Error</h1>
          <p className="text-content-secondary mb-4">{error}</p>
          <button onClick={initializeFlow} className="btn-primary">Try Again</button>
        </div>
      </div>
    );
  }

  const defaultNodes = flow?.ui.nodes.filter((n) => n.group === 'default') || [];
  const passwordNodes = flow?.ui.nodes.filter((n) => n.group === 'password') || [];
  const oidcNodes = flow?.ui.nodes.filter((n) => n.group === 'oidc') || [];

  const Header = () => (
    <div className="text-center mb-8">
      <img src={LOGO_MARK_LIGHT} alt={LOGO_ALT} className="h-16 w-16 mx-auto mb-4 dark:hidden" />
      <img src={LOGO_MARK_DARK} alt={LOGO_ALT} className="h-16 w-16 mx-auto mb-4 hidden dark:block" />
      <h1 className="text-2xl font-bold text-content-primary">Welcome Back</h1>
      <p className="text-content-secondary mt-1">Sign in to your account</p>
    </div>
  );

  const ErrorBanner = () => error ? (
    <div className="bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg mb-4 text-sm">
      {error}
    </div>
  ) : null;

  const providerLabel = (id: string): string => {
    const labels: Record<string, string> = {
      microsoft: 'Microsoft',
      google: 'Google',
      github: 'GitHub',
      apple: 'Apple',
      okta: 'Okta',
      slack: 'Slack',
    };
    return labels[id] || id.charAt(0).toUpperCase() + id.slice(1);
  };

  const providerIcon = (id: string) => {
    switch (id) {
      case 'microsoft':
        return (
          <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
            <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
            <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
          </svg>
        );
      case 'google':
        return (
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
          </svg>
        );
    }
  };

  const handleOidcLogin = (providerId: string) => {
    if (!flow) return;
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = flow.ui.action;
    form.style.display = 'none';

    const addField = (name: string, value: string) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      form.appendChild(input);
    };

    for (const node of flow.ui.nodes) {
      if (node.group === 'default' && (node.attributes as { type?: string }).type === 'hidden') {
        const a = node.attributes as UiNodeInputAttributes;
        addField(a.name, a.value as string);
      }
    }

    addField('provider', providerId);
    addField('method', 'oidc');

    document.body.appendChild(form);
    form.submit();
  };

  const OidcButtons = () => {
    if (oidcNodes.length === 0) return null;
    const providers = oidcNodes.filter(
      (n) => (n.attributes as UiNodeInputAttributes).name === 'provider'
    );
    if (providers.length === 0) return null;

    return (
      <>
        <div className="relative my-4">
          <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border"></div></div>
          <div className="relative flex justify-center text-xs"><span className="px-2 bg-surface-800 text-content-muted">or continue with</span></div>
        </div>
        <div className="space-y-2">
          {providers.map((node) => {
            const attrs = node.attributes as UiNodeInputAttributes;
            const id = attrs.value as string;
            return (
              <button
                key={id}
                type="button"
                onClick={() => handleOidcLogin(id)}
                disabled={isSubmitting}
                className="btn-secondary w-full flex items-center justify-center space-x-2"
              >
                {providerIcon(id)}
                <span>Sign in with {providerLabel(id)}</span>
              </button>
            );
          })}
        </div>
      </>
    );
  };

  const RegisterLinks = () => (
    <div className="mt-6 text-center space-y-2">
      <p className="text-content-secondary">Don&apos;t have an account?</p>
      <div className="flex flex-col sm:flex-row gap-2 justify-center">
        <Link to={`/register/personal?return_to=${encodeURIComponent(returnTo)}`}
          className="text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300 font-medium text-sm">
          Register as individual
        </Link>
        <span className="hidden sm:inline text-content-tertiary">|</span>
        <Link to={`/register/business?return_to=${encodeURIComponent(returnTo)}`}
          className="text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300 font-medium text-sm">
          Register as business
        </Link>
      </div>
    </div>
  );

  // ── Email OTP Code View ──
  if (loginPhase === 'code') {
    const handleCodeSubmit = async (e: React.FormEvent) => {
      e.preventDefault();
      if (!flow || !emailCode) return;
      setIsSubmitting(true);
      setError(null);
      try {
        const csrfNode = flow.ui.nodes.find(
          (n) => (n.attributes as UiNodeInputAttributes).name === 'csrf_token'
        );
        const csrf = csrfNode ? (csrfNode.attributes as UiNodeInputAttributes).value as string : '';
        const { data } = await kratos.updateLoginFlow({
          flow: flow.id,
          updateLoginFlowBody: {
            method: 'code',
            code: emailCode,
            identifier: identifier,
            csrf_token: csrf,
          } as any,  // eslint-disable-line @typescript-eslint/no-explicit-any
        });
        if (data.session) {
          if (isMobileLogin && mobileCallback) {
            await fetchMobileToken(mobileCallback);
            return;
          }
          window.location.href = returnTo;
        }
      } catch (err: unknown) {
        if (!handle422Redirect(err)) {
          setError(handleKratosError(err));
        }
      } finally {
        setIsSubmitting(false);
      }
    };

    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-accent-tint flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-content-primary">Check your email</h1>
            <p className="text-content-secondary mt-1">
              We sent a login code to <span className="font-medium">{identifier}</span>
            </p>
          </div>
          <FlowMessages messages={flow?.ui.messages} />
          <ErrorBanner />
          <form onSubmit={handleCodeSubmit} className="space-y-4">
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={emailCode}
              onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="block w-full text-center text-3xl tracking-[0.5em] font-mono px-4 py-4 border border-border rounded-lg bg-surface-800 text-content-primary focus:ring-accent focus:border-accent"
              maxLength={6}
              disabled={isSubmitting}
              autoFocus
            />
            <button type="submit" disabled={isSubmitting || emailCode.length !== 6}
              className="btn-primary w-full flex items-center justify-center">
              {isSubmitting ? <LoadingSpinner size="sm" /> : 'Sign in'}
            </button>
          </form>
          <div className="mt-4 text-center">
            <button onClick={() => { setLoginPhase('identifier'); setError(null); }}
              className="text-sm text-content-tertiary hover:text-content-secondary">
              Try a different method
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── TOTP View ──
  if (loginPhase === 'totp') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-orbital-100 dark:bg-orbital-900 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-orbital-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-content-primary">Two-Factor Authentication</h1>
            <p className="text-content-secondary mt-1">Enter the code from your authenticator app</p>
          </div>
          <FlowMessages messages={flow?.ui.messages} />
          <ErrorBanner />
          <form onSubmit={handleTOTPSubmit} className="space-y-4">
            <input id="totp-code" type="text" inputMode="numeric" autoComplete="one-time-code"
              value={totpCode} onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="block w-full text-center text-3xl tracking-[0.5em] font-mono px-4 py-4 border border-border rounded-lg focus:ring-orbital-500 focus:border-orbital-500"
              maxLength={6} disabled={isSubmitting} autoFocus />
            <button type="submit" disabled={isSubmitting || totpCode.length !== 6}
              className="btn-primary w-full flex items-center justify-center">
              {isSubmitting ? <LoadingSpinner size="sm" /> : 'Verify'}
            </button>
          </form>
          {isAal2 ? <SecondStepOptions current="totp" /> : (
            <div className="mt-4 text-center">
              <button onClick={() => setLoginPhase('lookup_secret')}
                className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300">
                Use a backup code instead
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Approve on phone View ──
  if (loginPhase === 'approve') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-content-primary">Approve on your phone</h1>
            <p className="text-content-secondary mt-1">
              Open the Authenticator app and confirm this number
            </p>
          </div>
          <ErrorBanner />
          {phoneApproval ? (
            <div className="text-center space-y-4">
              <div className="text-5xl font-mono font-bold tracking-widest text-content-primary">
                {phoneApproval.matchNumber}
              </div>
              {isSubmitting ? <LoadingSpinner size="sm" text="Signing you in..." /> : (
                <p className="text-sm text-content-tertiary">Waiting for your phone…</p>
              )}
            </div>
          ) : (
            <button type="button" onClick={startPhoneApproval} disabled={isSubmitting}
              className="btn-primary w-full flex items-center justify-center">
              {isSubmitting ? <LoadingSpinner size="sm" /> : 'Send to my phone again'}
            </button>
          )}
          <SecondStepOptions current="approve" />
        </div>
      </div>
    );
  }

  // ── Backup Code View ──
  if (loginPhase === 'lookup_secret') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-content-primary">Use Backup Code</h1>
            <p className="text-content-secondary mt-1">Enter one of your recovery codes</p>
          </div>
          <FlowMessages messages={flow?.ui.messages} />
          <ErrorBanner />
          <form onSubmit={handleLookupSubmit} className="space-y-4">
            <input type="text" value={lookupCode} onChange={(e) => setLookupCode(e.target.value)}
              placeholder="Enter backup code"
              className="block w-full text-center font-mono px-4 py-3 border border-border rounded-lg focus:ring-orbital-500 focus:border-orbital-500"
              disabled={isSubmitting} autoFocus />
            <button type="submit" disabled={isSubmitting || !lookupCode.trim()}
              className="btn-primary w-full flex items-center justify-center">
              {isSubmitting ? <LoadingSpinner size="sm" /> : 'Verify'}
            </button>
          </form>
          {isAal2 ? <SecondStepOptions current="lookup_secret" /> : (
            <div className="mt-4 text-center">
              <button onClick={() => setLoginPhase('totp')}
                className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300">
                Use authenticator app instead
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── WebAuthn View (passkey challenge ready) ──
  if (loginPhase === 'webauthn') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full">
          <Header />
          <FlowMessages messages={flow?.ui.messages} />
          <ErrorBanner />

          <div className="space-y-4">
            <p className="text-sm text-content-secondary text-center">
              A passkey is available for <strong>{identifier || flowIdentifier}</strong>
            </p>

            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => handlePasskeyLogin('device')}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-orbital-600 text-white rounded-lg font-medium hover:bg-orbital-700 transition-colors"
            >
              {isSubmitting ? <LoadingSpinner size="sm" /> : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                  </svg>
                  Use a passkey on this device
                </>
              )}
            </button>

            {phonePasskey === false ? (
              <p className="text-xs text-content-tertiary text-center">{PHONE_PASSKEY_UNAVAILABLE}</p>
            ) : <button
              type="button"
              disabled={isSubmitting}
              onClick={() => handlePasskeyLogin('phone')}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 border border-orbital-600 text-orbital-600 rounded-lg font-medium hover:bg-orbital-50 dark:hover:bg-orbital-900/30 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Use a passkey on my phone (scan QR)
            </button>}

            <div className="text-center">
              <button type="button" disabled={isSubmitting} onClick={() => handlePasskeyLogin('any')}
                className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300">
                Use a security key or another option
              </button>
            </div>

            {!isAal2 && <div className="text-center">
              <button onClick={async () => {
                // Create a fresh flow for password login — the webauthn flow doesn't have password fields
                setError(null);
                try {
                  const { data } = await kratos.createBrowserLoginFlow({ returnTo });
                  const newParams = new URLSearchParams();
                  newParams.set('flow', data.id);
                  if (isMobileLogin && mobileCallback) {
                    newParams.set('mobile', 'true');
                    newParams.set('callback', mobileCallback);
                  }
                  navigate(`/login?${newParams.toString()}`, { replace: true });
                  setFlow(data);
                  setLoginPhase('password');
                } catch {
                  setLoginPhase('password');
                }
              }}
                className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300">
                Use password instead
              </button>
            </div>}
          </div>
          {isAal2 ? <SecondStepOptions current="webauthn" /> : <RegisterLinks />}
        </div>
      </div>
    );
  }

  // ── QR Login success handler ──
  const handleQRSuccess = (userEmail?: string) => {
    void userEmail;
    // Session cookie was set by the gateway status endpoint.
    // Just redirect to the return URL.
    window.location.href = returnTo;
  };

  // ── Identifier View (email first) with QR tab ──
  if (loginPhase === 'identifier') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="card max-w-md w-full">
          <Header />

          {/* Login method tabs — only show on non-mobile flows */}
          {!isMobileLogin && (
            <div className="flex border-b border-border mb-6">
              <button
                onClick={() => setLoginTab('credentials')}
                className={`flex-1 pb-3 text-sm font-medium border-b-2 transition-colors ${
                  loginTab === 'credentials'
                    ? 'border-orbital-600 text-orbital-600'
                    : 'border-transparent text-content-tertiary hover:text-content-secondary'
                }`}
              >
                Email / Password
              </button>
              <button
                onClick={() => setLoginTab('qr')}
                className={`flex-1 pb-3 text-sm font-medium border-b-2 transition-colors ${
                  loginTab === 'qr'
                    ? 'border-orbital-600 text-orbital-600'
                    : 'border-transparent text-content-tertiary hover:text-content-secondary'
                }`}
              >
                QR Code
              </button>
            </div>
          )}

          {loginTab === 'qr' && !isMobileLogin ? (
            <QRLogin onSuccess={handleQRSuccess} returnTo={returnTo} />
          ) : (
            <>
              <FlowMessages messages={flow?.ui.messages} />
              <ErrorBanner />

              <form onSubmit={handleIdentifierSubmit} className="space-y-4">
                {/* Hidden fields */}
                {defaultNodes
                  .filter((n) => (n.attributes as { type?: string }).type === 'hidden')
                  .map((node, i) => (
                    <FormField key={i} node={node} disabled={isSubmitting} />
                  ))}

                {/* Email field */}
                <div className="space-y-1">
                  <label htmlFor="identifier" className="block text-sm font-medium text-content-primary">Email</label>
                  <input
                    id="identifier"
                    name="identifier"
                    type="email"
                    required
                    autoComplete="email"
                    autoFocus
                    value={identifier}
                    onChange={(e) => setIdentifier(e.target.value)}
                    placeholder="you@example.com"
                    disabled={isSubmitting}
                    className="input-field"
                  />
                </div>

                <button type="submit" disabled={isSubmitting || !identifier.trim()}
                  className="btn-primary w-full flex items-center justify-center">
                  {isSubmitting ? <LoadingSpinner size="sm" /> : 'Continue with password'}
                </button>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border"></div></div>
                  <div className="relative flex justify-center text-xs"><span className="px-2 bg-surface-800 text-content-muted">or</span></div>
                </div>

                <button type="button" disabled={isSubmitting || !identifier.trim()}
                  onClick={async () => {
                    if (!identifier.trim()) return;
                    setIsSubmitting(true);
                    try {
                      const { data } = await kratos.createBrowserLoginFlow({
                        refresh: false,
                        returnTo: returnTo,
                      });
                      const resp = await kratos.updateLoginFlow({
                        flow: data.id,
                        updateLoginFlowBody: {
                          method: 'code',
                          identifier: identifier,
                          csrf_token: data.ui.nodes.find(
                            (n) => (n.attributes as UiNodeInputAttributes).name === 'csrf_token'
                          )?.attributes && ((data.ui.nodes.find(
                            (n) => (n.attributes as UiNodeInputAttributes).name === 'csrf_token'
                          )?.attributes as UiNodeInputAttributes).value as string) || '',
                        } as any,  // eslint-disable-line @typescript-eslint/no-explicit-any
                      });
                      const respData = resp.data as unknown as LoginFlow;
                      if (respData.ui) {
                        setFlow(respData);
                        setLoginPhase(detectLoginPhase(respData));
                      }
                    } catch (err: unknown) {
                      if (!handle422Redirect(err)) {
                        const errData = (err as { response?: { data?: LoginFlow } })?.response?.data;
                        if (errData?.ui) {
                          setFlow(errData);
                          setLoginPhase(detectLoginPhase(errData));
                        } else {
                          setError(handleKratosError(err));
                        }
                      }
                    } finally {
                      setIsSubmitting(false);
                    }
                  }}
                  className="btn-secondary w-full flex items-center justify-center space-x-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Sign in with email code</span>
                </button>

                <OidcButtons />
              </form>
            </>
          )}

          <RegisterLinks />
        </div>
      </div>
    );
  }

  // ── Password View ──
  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="card max-w-md w-full">
        <Header />

        {/* Login method tabs — only show on non-mobile flows */}
        {!isMobileLogin && (
          <div className="flex border-b border-border mb-6">
            <button
              onClick={() => setLoginTab('credentials')}
              className={`flex-1 pb-3 text-sm font-medium border-b-2 transition-colors ${
                loginTab === 'credentials'
                  ? 'border-orbital-600 text-orbital-600'
                  : 'border-transparent text-content-tertiary hover:text-content-secondary'
              }`}
            >
              Email / Password
            </button>
            <button
              onClick={() => setLoginTab('qr')}
              className={`flex-1 pb-3 text-sm font-medium border-b-2 transition-colors ${
                loginTab === 'qr'
                  ? 'border-orbital-600 text-orbital-600'
                  : 'border-transparent text-content-tertiary hover:text-content-secondary'
              }`}
            >
              QR Code
            </button>
          </div>
        )}

        {loginTab === 'qr' && !isMobileLogin ? (
          <QRLogin onSuccess={handleQRSuccess} returnTo={returnTo} />
        ) : (
          <>
            <FlowMessages messages={flow?.ui.messages} />
            <ErrorBanner />

            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              {/* Hidden fields */}
              {defaultNodes
                .filter((n) => (n.attributes as { type?: string }).type === 'hidden')
                .map((node, i) => (
                  <FormField key={i} node={node} disabled={isSubmitting} />
                ))}

              {/* Identifier — show if not already entered */}
              {identifier ? (
                <div className="flex items-center justify-between bg-surface-secondary rounded-lg px-4 py-3">
                  <div>
                    <span className="text-sm text-content-tertiary">Signing in as</span>
                    <p className="text-sm font-medium text-content-primary">{identifier}</p>
                  </div>
                  <button type="button" onClick={() => { setIdentifier(''); setLoginPhase('identifier'); setError(null); }}
                    className="text-sm text-orbital-600 hover:text-orbital-800">Change</button>
                  <input type="hidden" name="identifier" value={identifier} />
                </div>
              ) : (
                defaultNodes
                  .filter((n) => (n.attributes as { type?: string }).type !== 'hidden')
                  .map((node, i) => (
                    <FormField key={i} node={node} disabled={isSubmitting} />
                  ))
              )}

              {/* Password field */}
              {passwordNodes
                .filter((n) => (n.attributes as { type?: string }).type !== 'submit')
                .map((node, i) => (
                  <FormField key={i} node={node} disabled={isSubmitting} />
                ))}

              <input type="hidden" name="method" value="password" />

              <div className="text-right">
                <Link to="/recovery"
                  className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300">
                  Forgot password?
                </Link>
              </div>

              <button type="submit" disabled={isSubmitting}
                className="btn-primary w-full flex items-center justify-center">
                {isSubmitting ? <LoadingSpinner size="sm" /> : 'Sign In'}
              </button>
              <OidcButtons />
            </form>

            {/* Switch to passkey if available */}
            {hasPasskey && (
              <div className="mt-3 text-center">
                <button onClick={() => setLoginPhase('webauthn')}
                  className="text-sm text-orbital-600 hover:text-orbital-800 dark:text-orbital-400 dark:hover:text-orbital-300">
                  Use passkey instead
                </button>
              </div>
            )}
          </>
        )}

        <RegisterLinks />
      </div>
    </div>
  );
}
